"use client"

import { motion } from "framer-motion"
import { Navbar } from "@/components/layout/navbar"
import { Footer } from "@/components/layout/footer"

const metrics = [
  {
    name: "Attention Distribution",
    question: "What pulls your focus — when nobody's directing it?",
    method:
      "We use BERTopic clustering to map where your cognitive energy goes across conversations. The result: a topographic map of what you actually care about, not what you say you care about.",
  },
  {
    name: "Learning Rate",
    question: "How fast does your thinking evolve?",
    method:
      "We track semantic displacement — how your conversation topics shift over time. High velocity means rapid exploration. Low velocity means deep commitment to specific territory. Neither is better.",
  },
  {
    name: "Loss Landscape",
    question: "Are you stuck, or transforming?",
    method:
      "Inspired by optimization theory: we measure topic diversity against transition predictability. The result reveals whether you're in a plateau, a valley, or approaching a phase transition in your thinking.",
  },
  {
    name: "Coherence",
    question: "How integrated is your mind?",
    method:
      "Cross-domain embedding similarity shows whether your different interests inform each other or exist in separate silos. High coherence means your thinking has internal structure. Low coherence means you're exploring in parallel tracks.",
  },
  {
    name: "Gradient Signal",
    question: "Where is your thinking actually heading?",
    method:
      "Directional analysis of topic evolution over time. Are your interests converging toward something specific, or diffusing outward? This is the trajectory beneath the noise.",
  },
]

export default function SciencePage() {
  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-[var(--bg-page)] pt-28 pb-20">
        <div className="mx-auto max-w-3xl px-6">
          {/* Section 1: Opening */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-[var(--font-instrument-serif)] text-4xl font-normal text-[var(--text-primary)] sm:text-5xl">
              The math behind the mirror.
            </h1>
            <p className="mt-4 text-xl text-[var(--text-secondary)]">
              How computational analysis turns conversations into cognitive portraits.
            </p>
            <p className="mt-6 text-base leading-relaxed text-[var(--text-secondary)]">
              Lumensis uses methods from natural language processing and information
              theory to find patterns in your AI conversations &mdash; patterns that
              are invisible from inside them. Here&apos;s what we measure, and how.
            </p>
          </motion.div>

          {/* Section 2: What We Measure */}
          <motion.div
            className="mt-20"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <h2 className="font-[var(--font-instrument-serif)] text-3xl font-normal text-[var(--text-primary)]">
              Five dimensions of thinking.
            </h2>
          </motion.div>

          <div className="mt-12 space-y-10">
            {metrics.map((metric, i) => (
              <motion.div
                key={metric.name}
                className="rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-white p-6 shadow-[var(--shadow-card)]"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.25 + i * 0.06 }}
              >
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--gold)] mb-2">
                  {metric.name}
                </p>
                <h3 className="font-[var(--font-instrument-serif)] text-xl font-normal text-[var(--text-primary)] mb-3">
                  {metric.question}
                </h3>
                <p className="text-sm leading-relaxed text-[var(--text-secondary)]">
                  {metric.method}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Section 3: Honest Framing */}
          <motion.div
            className="mt-24"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <h2 className="font-[var(--font-instrument-serif)] text-3xl font-normal text-[var(--text-primary)] mb-6">
              What this is &mdash; and isn&apos;t.
            </h2>

            <div className="space-y-4 text-base leading-relaxed text-[var(--text-secondary)]">
              <p>
                Lumensis is conversational pattern exploration, not a validated
                psychological assessment.
              </p>
              <p>
                Our methods &mdash; BERTopic topic modeling, KL and Jensen-Shannon
                divergence, transformer embeddings, entropy analysis &mdash; are
                mathematically rigorous. They measure real patterns in real data.
              </p>
              <p>
                What they don&apos;t do is map neatly onto established psychological
                constructs. We haven&apos;t run external validation studies (yet). The
                metrics describe your conversational behavior, not your personality.
              </p>
              <p>
                The real insight comes from what these patterns reveal when you see
                them together &mdash; and from the AI-powered narrative layer that
                translates numbers into observations about your thinking.
              </p>
              <p>
                Think of it as a new kind of mirror: precise about what it shows
                you, honest about what it can&apos;t.
              </p>
            </div>

            <div className="mt-10 space-y-2 text-sm text-[var(--text-muted)]">
              <p>
                Have questions about our methodology?{" "}
                <a
                  href="mailto:hello@lumens.is"
                  className="text-[var(--gold)] hover:underline"
                >
                  hello@lumens.is
                </a>
              </p>
            </div>
          </motion.div>
        </div>
      </main>
      <Footer />
    </>
  )
}
